import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type FilterKey = 'all' | 'tvc' | 'mv' | 'digital' | 'exhibition' | 'drama';

export default function CasesSection() {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all');

  const filters: { key: FilterKey; labelKey: string }[] = [
    { key: 'all', labelKey: 'cases.filterAll' },
    { key: 'tvc', labelKey: 'cases.filterTvc' },
    { key: 'mv', labelKey: 'cases.filterMv' },
    { key: 'digital', labelKey: 'cases.filterDigital' },
    { key: 'exhibition', labelKey: 'cases.filterExhibition' },
    { key: 'drama', labelKey: 'cases.filterDrama' },
  ];

  const cases = [
    {
      id: 1,
      titleKey: 'cases.case1.title',
      category: 'tvc' as FilterKey,
      descKey: 'cases.case1.desc',
      tags: ['cases.case1.tag1', 'cases.case1.tag2', 'cases.case1.tag3'],
      featured: true,
      image: 'https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/d17964909444b3508d6750c722477edd.jpeg'
    },
    {
      id: 2,
      titleKey: 'cases.case2.title',
      category: 'mv' as FilterKey,
      descKey: 'cases.case2.desc',
      tags: ['cases.case2.tag1', 'cases.case2.tag2', 'cases.case2.tag3'],
      featured: false,
      image: 'https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/d8d2b1b5cd08287aa04fc9680c72306f.png'
    },
    {
      id: 3,
      titleKey: 'cases.case3.title',
      category: 'exhibition' as FilterKey,
      descKey: 'cases.case3.desc',
      tags: ['cases.case3.tag1', 'cases.case3.tag2', 'cases.case3.tag3'],
      featured: false,
      image: 'https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/be5fd0e09afb840c7006df7cada375ab.png'
    },
    {
      id: 4,
      titleKey: 'cases.case4.title',
      category: 'tvc' as FilterKey,
      descKey: 'cases.case4.desc',
      tags: ['cases.case4.tag1', 'cases.case4.tag2', 'cases.case4.tag3'],
      featured: false,
      image: 'https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/31abbcfaca6a6ae62ed42e6f1dded5bc.png'
    },
    {
      id: 5,
      titleKey: 'cases.case5.title',
      category: 'digital' as FilterKey,
      descKey: 'cases.case5.desc',
      tags: ['cases.case5.tag1', 'cases.case5.tag2', 'cases.case5.tag3'],
      featured: false,
      image: 'https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/3e7df587ceacad3602d3e8f21b2840e7.png'
    },
    {
      id: 6,
      titleKey: 'cases.case6.title',
      category: 'drama' as FilterKey,
      descKey: 'cases.case6.desc',
      tags: ['cases.case6.tag1', 'cases.case6.tag2', 'cases.case6.tag3'],
      featured: false,
      image: 'https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/6ce61a34862b2d730f3afa329d59660a.jpeg'
    },
    {
      id: 7,
      titleKey: 'cases.case7.title',
      category: 'drama' as FilterKey,
      descKey: 'cases.case7.desc',
      tags: ['cases.case7.tag1', 'cases.case7.tag2', 'cases.case7.tag3'],
      featured: false,
      image: 'https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/6d5b30099d5cc53f847dfa46ce2d49ce.png'
    }
  ];

  const filteredCases = activeFilter === 'all'
    ? cases
    : cases.filter(c => c.category === activeFilter);

  const featuredCase = filteredCases.length > 0
    ? (filteredCases.find(c => c.featured) || filteredCases[0])
    : null;
  const otherCases = featuredCase
    ? filteredCases.filter(c => c.id !== featuredCase.id)
    : [];

  return (
    <section id="cases" className="relative py-24 bg-gradient-to-b from-[#0a0118] to-[#16213e] overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6">
          <div>
            <div className="inline-block px-4 py-2 bg-[#00ff41]/10 border border-[#00ff41] text-[#00ff41] text-xs font-['JetBrains_Mono'] font-semibold mb-6"
              style={{ clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))' }}
            >
              {t('cases.sectionTag')}
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-white font-['Space_Grotesk']">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ff41] to-[#00d9ff]">
                {t('cases.title')}
              </span>
            </h2>
          </div>

          <div className="flex flex-wrap gap-3">
            {filters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-4 py-2 text-sm font-['JetBrains_Mono'] font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeFilter === filter.key
                    ? 'bg-[#00ff41] text-black'
                    : 'bg-transparent border border-gray-600 text-gray-400 hover:border-[#00ff41] hover:text-[#00ff41]'
                }`}
                style={{ clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))' }}
              >
                {t(filter.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {filteredCases.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-gray-500 font-['JetBrains_Mono']">
            <i className="ri-folder-open-line text-5xl mb-4 text-gray-700"></i>
            <p className="text-lg">{t('cases.noResults')}</p>
            <p className="text-sm mt-2 text-gray-600">{t('cases.noResultsDesc')}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {featuredCase && (
              <div className="md:col-span-2 md:row-span-2 group relative overflow-hidden bg-[#1a0b2e]/50 border-2 border-[#00ff41]/50 hover:border-[#00ff41] transition-all duration-500"
                style={{ clipPath: 'polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px))' }}
              >
                <div className="absolute top-4 left-4 z-20 px-3 py-1 bg-[#00ff41] text-black text-xs font-['JetBrains_Mono'] font-bold"
                  style={{ clipPath: 'polygon(0 0, calc(100% - 4px) 0, 100% 4px, 100% 100%, 4px 100%, 0 calc(100% - 4px))' }}
                >
                  {t('cases.featured')}
                </div>
                <div className="relative h-full min-h-[500px]">
                  <img src={featuredCase.image} alt={t(featuredCase.titleKey)} className="w-full h-full object-cover" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-8">
                    <div className="text-[#00ff41] font-['JetBrains_Mono'] text-xs mb-2">
                      // {featuredCase.category.toUpperCase()}
                    </div>
                    <h3 className="text-3xl font-bold text-white font-['Space_Grotesk'] mb-2">{t(featuredCase.titleKey)}</h3>
                    <p className="text-gray-300 mb-4">{t(featuredCase.descKey)}</p>
                    <div className="flex flex-wrap gap-2">
                      {featuredCase.tags.map((tagKey, idx) => (
                        <span key={idx} className="px-3 py-1 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-['JetBrains_Mono']"
                          style={{ clipPath: 'polygon(0 0, calc(100% - 4px) 0, 100% 4px, 100% 100%, 4px 100%, 0 calc(100% - 4px))' }}
                        >
                          {t(tagKey)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {otherCases.slice(0, 5).map((caseItem) => (
              <div key={caseItem.id}
                className="group relative overflow-hidden bg-[#1a0b2e]/30 border border-gray-700/50 hover:border-[#00d9ff] transition-all duration-500 hover:scale-105"
                style={{ clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))' }}
              >
                <div className="relative h-64">
                  <img src={caseItem.image} alt={t(caseItem.titleKey)} className="w-full h-full object-cover" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                    <div className="text-[#00d9ff] font-['JetBrains_Mono'] text-xs mb-1">
                      // {caseItem.category.toUpperCase()}
                    </div>
                    <h4 className="text-lg font-bold text-white font-['Space_Grotesk'] mb-1">{t(caseItem.titleKey)}</h4>
                    <p className="text-gray-400 text-xs mb-2">{t(caseItem.descKey)}</p>
                    <div className="flex flex-wrap gap-1">
                      {caseItem.tags.slice(0, 2).map((tagKey, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-white/10 text-white text-xs font-['JetBrains_Mono']"
                          style={{ clipPath: 'polygon(0 0, calc(100% - 3px) 0, 100% 3px, 100% 100%, 3px 100%, 0 calc(100% - 3px))' }}
                        >
                          {t(tagKey)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
